import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A page's main actions — page headers, the patient banner, the doctor's home — are 36px with 14px text (40px on
 * touch screens), a step up from the 32px buttons inside cards and tables. Put this on the actions' container; it
 * sizes the direct children (buttons, links styled as buttons, icon-only buttons at least square, Segmented switches),
 * so callers keep passing size="sm" buttons.
 */
export const PAGE_ACTIONS_CLASS = cn(
  // Icon-only buttons (px-0) keep no padding, or it squeezes their icon
  "[&>button]:h-9 [&>button:not(.px-0)]:px-3.5 [&>button]:text-sm touch:[&>button]:h-10",
  "[&>a]:h-9 [&>a]:px-3.5 [&>a]:text-sm touch:[&>a]:h-10",
  // Icon-only buttons (w-8 px-0) become 36px squares; text buttons are wider anyway
  "[&>button]:min-w-9 touch:[&>button]:min-w-10",
  "[&>[role=group]]:h-9 touch:[&>[role=group]]:h-10",
  // A search box in the actions (input wrapped with its icon)
  "[&>div>input]:h-9 touch:[&>div>input]:h-10",
);

interface PageHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle?: React.ReactNode;
  /** Right-hand controls; pass size="sm" buttons — the header sizes them up to the page-action size. */
  actions?: React.ReactNode;
  className?: string;
}

/** Title row shared by the top-level list pages: icon tile, title, optional subtitle, actions. Wraps on phones. */
export function PageHeader({ icon: Icon, title, subtitle, actions, className }: PageHeaderProps) {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3", className)}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.01em] text-foreground truncate">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className={cn("flex flex-wrap items-center gap-2", PAGE_ACTIONS_CLASS)}>{actions}</div>}
    </div>
  );
}
